import {
    Building2,
    CalendarRange,
    CheckCircle2,
    ClipboardList,
    Filter,
    RotateCcw,
    SlidersHorizontal,
    X,
    XCircle,
} from "lucide-react";

import clsx from "clsx";
import { useMemo, useState } from "react";
import {
    COACH_ASSIGNMENT_STATUSES,
    type CoachAssignment,
    type CoachAssignmentStatus,
} from "@flexv-coach/shared";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { MenuSelect } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { EmptyState, ErrorState, LoadingBlock } from "@/components/ui/States";
import { addDays, formatDayMonth, formatTimeRange, startOfWeek, todayIso } from "@/lib/date";
import { ApiError } from "@/lib/api";
import { roleLabel, sourceLabel, statusLabel, statusTone } from "@/lib/labels";
import { useAuth } from "@/providers/AuthProvider";
import { useI18n } from "@/hooks/useI18n";
import { useUpdateAssignmentStatus } from "@/features/plan/useCoachPlan";
import { useCoachAssignments } from "./useCoachAssignments";

/** Aksi cepat per status — aturannya sama dengan menu Plan. */
const ACTIONS_BY_STATUS: Record<string, CoachAssignmentStatus[]> = {
    scheduled: ["confirmed", "cancelled"],
    confirmed: ["completed", "cancelled"],
    ongoing: ["completed", "cancelled"],
    completed: [],
    cancelled: [],
    no_show: [],
};

export function AssignmentsPage() {
    const { coach } = useAuth();
    const { t } = useI18n();

    const [from, setFrom] = useState(() => startOfWeek(todayIso()));
    const [to, setTo] = useState(() => addDays(startOfWeek(todayIso()), 6));
    const [outletId, setOutletId] = useState("");
    const [status, setStatus] = useState<CoachAssignmentStatus | "">("");
    const [upcomingOnly, setUpcomingOnly] = useState(false);
    /** "custom" bila tanggal diubah manual — preset lalu tidak lagi aktif. */
    const [preset, setPreset] = useState<"week" | "month" | "custom">("week");
    const [actionError, setActionError] = useState<string | null>(null);

    const { data, isPending, isError, error, refetch } = useCoachAssignments({
        from,
        to,
        outletId: outletId || undefined,
        status: status || undefined,
        upcomingOnly,
    });
    const updateStatus = useUpdateAssignmentStatus();

    const outletOptions = useMemo(() => {
        const map = new Map<string, string>();
        for (const affiliation of coach?.affiliations ?? []) {
            if (affiliation.outlet) map.set(affiliation.outlet.id, affiliation.outlet.name);
        }
        return [
            { value: "", label: t("assignments.filter.allOutlets") },
            ...[...map].map(([value, label]) => ({ value, label })),
        ];
    }, [coach, t]);

    const statusOptions = [
        { value: "", label: t("assignments.filter.allStatuses") },
        ...COACH_ASSIGNMENT_STATUSES.map((value) => ({ value, label: statusLabel(value) })),
    ];

    const applyPreset = (value: string) => {
        const days = value === "week" ? 7 : 30;
        const start = days === 7 ? startOfWeek(todayIso()) : todayIso();
        setFrom(start);
        setTo(addDays(start, days - 1));
        setPreset(value === "week" ? "week" : "month");
    };

    const setDate = (which: "from" | "to", value: string) => {
        setPreset("custom");
        if (which === "from") setFrom(value);
        else setTo(value);
    };

    const resetFilters = () => {
        applyPreset("week");
        setOutletId("");
        setStatus("");
        setUpcomingOnly(false);
    };

    const activeChips = useMemo(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (outletId) {
            const match = outletOptions.find((option) => option.value === outletId);
            chips.push({
                key: "outlet",
                label: match?.label ?? outletId,
                onClear: () => setOutletId(""),
            });
        }
        if (status) {
            chips.push({
                key: "status",
                label: statusLabel(status),
                onClear: () => setStatus(""),
            });
        }
        if (upcomingOnly) {
            chips.push({
                key: "upcoming",
                label: t("assignments.filter.upcomingOnly"),
                onClear: () => setUpcomingOnly(false),
            });
        }
        return chips;
    }, [outletId, outletOptions, status, upcomingOnly, t]);

    const isDirty = activeChips.length > 0 || preset === "custom";

    const handleAction = async (assignment: CoachAssignment, next: CoachAssignmentStatus) => {
        setActionError(null);
        try {
            await updateStatus.mutateAsync({ assignmentId: assignment.id, status: next });
        } catch (caught) {
            setActionError(
                caught instanceof ApiError ? caught.message : t("assignments.actionFailed"),
            );
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="app-text-primary text-2xl font-bold sm:text-3xl">
                        {t("assignments.title")}
                    </h1>
                    <p className="app-shell-text-muted mt-1 text-sm">
                        {t("assignments.subtitle")}
                    </p>
                </div>

            </header>

            <Card className="overflow-hidden">
                <CardContent className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="app-text-soft inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
                            <SlidersHorizontal className="h-3.5 w-3.5 app-accent-text" />
                            {t("assignments.filter.title")}
                        </span>

                        <SegmentedControl
                            label={t("assignments.filter.range")}
                            options={[
                                { value: "week", label: t("assignments.preset.week") },
                                { value: "month", label: t("assignments.preset.month") },
                            ]}
                            value={preset === "custom" ? "" : preset}
                            onChange={applyPreset}
                        />

                        <div className="app-content-subtle flex h-10 shrink-0 items-center gap-2 rounded-xl border app-shell-border px-3 transition-colors focus-within:border-[var(--color-primary)]/60">
                            <CalendarRange className="app-shell-text-muted h-4 w-4 shrink-0" />
                            <input
                                type="date"
                                aria-label={t("assignments.filter.from")}
                                value={from}
                                max={to}
                                onChange={(event) => setDate("from", event.target.value)}
                                className="app-date-input app-text-primary w-[8.5rem] bg-transparent text-sm outline-none"
                            />
                            <span className="app-text-soft text-xs">—</span>
                            <input
                                type="date"
                                aria-label={t("assignments.filter.to")}
                                value={to}
                                min={from}
                                onChange={(event) => setDate("to", event.target.value)}
                                className="app-date-input app-text-primary w-[8.5rem] bg-transparent text-sm outline-none"
                            />
                        </div>

                        <div className="app-content-subtle flex h-10 shrink-0 items-center gap-2 rounded-xl border app-shell-border px-3 transition-colors focus-within:border-[var(--color-primary)]/60">
                            <Building2 className="app-shell-text-muted h-4 w-4 shrink-0" />
                            <MenuSelect
                                label={t("assignments.filter.outlet")}
                                options={outletOptions}
                                value={outletId}
                                onChange={setOutletId}
                                className="w-[10rem]"
                            />
                        </div>

                        <div className="app-content-subtle flex h-10 shrink-0 items-center gap-2 rounded-xl border app-shell-border px-3 transition-colors focus-within:border-[var(--color-primary)]/60">
                            <Filter className="app-shell-text-muted h-4 w-4 shrink-0" />
                            <MenuSelect
                                label={t("assignments.filter.status")}
                                options={statusOptions}
                                value={status}
                                onChange={(next) => setStatus(next as CoachAssignmentStatus | "")}
                                className="w-[9rem]"
                            />
                        </div>

                        <button
                            type="button"
                            role="switch"
                            aria-checked={upcomingOnly}
                            onClick={() => setUpcomingOnly((prev) => !prev)}
                            className={clsx(
                                "inline-flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-xl border px-3 text-xs font-semibold transition-all duration-200",
                                upcomingOnly
                                    ? // Sama dengan gaya "aktif" di tempat lain
                                      // (mis. tombol "Minggu ini"): tint halus, bukan
                                      // balok solid.
                                      "app-accent-soft app-accent-text border-transparent shadow-sm"
                                    : "app-content-subtle app-shell-text-muted app-shell-border hover:app-content-subtle-hover",
                            )}
                        >
                            <span
                                className={clsx(
                                    "relative h-4 w-7 rounded-full transition-colors duration-200",
                                    // Track solid saat aktif supaya knob putih
                                    // benar-benar kontras (sebelumnya white/40,
                                    // jadi knob-nya nyamar).
                                    upcomingOnly
                                        ? "bg-[var(--color-primary)]"
                                        : "bg-[var(--app-content-subtle-hover)]",
                                )}
                            >
                                <span
                                    className={clsx(
                                        // Knob selalu putih + shadow tipis supaya
                                        // terbaca di track gelap maupun terang.
                                        "absolute top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform duration-200",
                                        upcomingOnly
                                            ? "translate-x-3.5"
                                            : "translate-x-0.5",
                                    )}
                                />
                            </span>
                            {t("assignments.filter.upcomingOnly")}
                        </button>

                        {isDirty && (
                            <Button
                                variant="ghost"
                                size="sm"
                                icon={<RotateCcw className="h-3.5 w-3.5" />}
                                onClick={resetFilters}
                            >
                                {t("assignments.filter.reset")}
                            </Button>
                        )}
                    </div>

                    {activeChips.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2 border-t app-shell-border pt-3">
                            <span className="app-text-soft text-xs font-semibold uppercase tracking-wide">
                                {t("assignments.filter.active")}
                            </span>
                            {activeChips.map((chip) => (
                                <button
                                    key={chip.key}
                                    type="button"
                                    onClick={chip.onClear}
                                    className="app-accent-soft app-accent-text group inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-opacity hover:opacity-80"
                                >
                                    {chip.label}
                                    <X className="h-3 w-3 opacity-60 transition-opacity group-hover:opacity-100" />
                                </button>
                            ))}
                        </div>
                    )}

                    {actionError && <Alert tone="error">{actionError}</Alert>}
                </CardContent>
            </Card>

            <Card>
                <CardHeader
                    title={t("assignments.listTitle")}
                    description={data ? t("assignments.listCount", { count: data.total }) : undefined}
                    icon={<ClipboardList className="h-5 w-5" />}
                />
                <CardContent noPadding>
                    {isPending && <LoadingBlock label={t("assignments.loading")} />}

                    {isError && (
                        <ErrorState
                            message={
                                error instanceof ApiError
                                    ? error.message
                                    : t("assignments.loadFailed")
                            }
                            onRetry={() => void refetch()}
                        />
                    )}

                    {data && data.items.length === 0 && (
                        <EmptyState
                            icon={<CalendarRange className="h-6 w-6" />}
                            title={t("assignments.empty.title")}
                            description={t("assignments.empty.description")}
                        />
                    )}

                    {data && data.items.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead>
                                    <tr className="app-text-soft border-b app-shell-border text-xs uppercase tracking-wide">
                                        <th className="px-5 py-3 font-semibold">
                                            {t("assignments.column.date")}
                                        </th>
                                        <th className="px-5 py-3 font-semibold">
                                            {t("assignments.column.session")}
                                        </th>
                                        <th className="px-5 py-3 font-semibold">
                                            {t("assignments.column.outlet")}
                                        </th>
                                        <th className="px-5 py-3 font-semibold">
                                            {t("assignments.column.status")}
                                        </th>
                                        <th className="px-5 py-3 text-right font-semibold">
                                            {t("assignments.column.actions")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y app-shell-border">
                                    {data.items.map((assignment) => {
                                        const actions = ACTIONS_BY_STATUS[assignment.status] ?? [];
                                        return (
                                            <tr
                                                key={assignment.id}
                                                className="transition-colors hover:app-content-subtle-hover"
                                            >
                                                <td className="app-text-primary whitespace-nowrap px-5 py-4">
                                                    <p className="font-semibold">
                                                        {formatDayMonth(assignment.assignmentDate)}
                                                    </p>
                                                    <p className="app-shell-text-muted text-xs">
                                                        {formatTimeRange(
                                                            assignment.startTime,
                                                            assignment.endTime,
                                                        )}
                                                    </p>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <p className="app-text-primary font-medium">
                                                        {assignment.sourceName ??
                                                            assignment.sport?.name ??
                                                            t("assignments.unnamed")}
                                                    </p>
                                                    <p className="app-text-soft text-xs">
                                                        {roleLabel(assignment.role)} ·{" "}
                                                        {sourceLabel(assignment.sourceType)}
                                                    </p>
                                                </td>
                                                <td className="app-shell-text-muted px-5 py-4">
                                                    {assignment.outlet?.name ?? "—"}
                                                </td>
                                                <td className="px-5 py-4">
                                                    <Badge tone={statusTone(assignment.status)}>
                                                        {statusLabel(assignment.status)}
                                                    </Badge>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {actions.length === 0 && (
                                                            <span className="app-text-soft text-xs">
                                                                —
                                                            </span>
                                                        )}
                                                        {actions.map((next) => (
                                                            <Button
                                                                key={next}
                                                                size="sm"
                                                                variant={
                                                                    next === "cancelled"
                                                                        ? "danger"
                                                                        : next === "completed"
                                                                          ? "primary"
                                                                          : "outline"
                                                                }
                                                                icon={
                                                                    next === "cancelled" ? (
                                                                        <XCircle className="h-3.5 w-3.5" />
                                                                    ) : (
                                                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                                                    )
                                                                }
                                                                loading={
                                                                    updateStatus.isPending
                                                                }
                                                                onClick={() =>
                                                                    void handleAction(
                                                                        assignment,
                                                                        next,
                                                                    )
                                                                }
                                                            >
                                                                {t(`assignments.action.${next}`)}
                                                            </Button>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
